import React from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { captureException } from '@sentry/react'
import { theme } from '../../../theme/theme'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { eventAttendance } from '../../../api/events'
import { boardRoomEventAttendance } from '../../../api/boardroom-events'
import { FormControl } from '../../common/FormControl'
import { TextInput } from '../../common/TextInput'
import { IconChoices } from '../../common/Icon'
import { EventAttendanceButton } from '../../../views/society/EventDetailView/EventAttendanceButtons'
import { IEventAttendanceStatus } from '../../../interfaces/models/events.interfaces'
import { PostEventCard } from '../../../generated/api/post-row'
import {
  invalidatePostRows,
  updateEventAttendance,
  viewerAttendance,
} from '../postRowCache'
import { useAuthenticatedUserId } from '../../../hooks/useAuthenticatedUserId'

interface PostEventAttendanceProps {
  event: PostEventCard
  disabled?: boolean
}

export const PostEventAttendance = ({
  event,
  disabled,
}: PostEventAttendanceProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const queryClient = useQueryClient()
  const userId = useAuthenticatedUserId() as string
  const { viewerStatus, viewerComment } = event.attendance
  const { control, watch, formState, reset } = useForm({
    mode: 'all',
    defaultValues: { comment: viewerComment || '' },
  })

  const { mutate: updateAttendance, isPending } = useMutation({
    mutationFn: async (status: IEventAttendanceStatus) => {
      const body = { status, comment: watch('comment') }
      const response =
        event.kind === 'boardroom'
          ? await boardRoomEventAttendance(event.id, body)
          : await eventAttendance(event.id, body)
      return response.data.data
    },
    meta: { skipGlobalInvalidate: true },
    onSuccess: (answered) => {
      reset({ comment: watch('comment') })
      if (answered == null) {
        invalidatePostRows(queryClient)
        return
      }
      updateEventAttendance(
        queryClient,
        event.id,
        viewerAttendance(answered, userId)
      )
    },
    onError: (err) => {
      captureException(err)
      invalidatePostRows(queryClient)
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [
          { scope: event.kind === 'boardroom' ? 'boardroomEvents' : 'events' },
        ],
      })
    },
  })

  const userAttending = viewerStatus === 'attending'
  const userNotAttending = viewerStatus === 'not_attending'
  const isUpdatingAttending = formState.isDirty && userAttending
  const isUpdatingNotAttending = formState.isDirty && userNotAttending

  const disableAttendingButton = (): boolean => {
    if (isPending || disabled) return true
    if (isUpdatingAttending) return false
    return userAttending
  }
  const disableNotAttendingButton = (): boolean => {
    if (isPending || disabled) return true
    if (isUpdatingNotAttending) return false
    return userNotAttending
  }

  return (
    <div>
      <Controller
        control={control}
        name="comment"
        render={({ field: { value, name, onChange, onBlur } }) => (
          <FormControl
            name={name}
            className="grow"
            label={translate('common.eventActions.attendance.comment')}
          >
            <TextInput
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              disabled={isPending || disabled}
              className="max-w-120 mb-2"
            />
          </FormControl>
        )}
      />
      <div className="flex gap-2">
        <EventAttendanceButton
          label={
            isUpdatingAttending
              ? translate('common.eventActions.attendance.updateComment')
              : translate('common.eventActions.attendance.attending')
          }
          icon={IconChoices.CHECKMARK}
          iconColor={theme.colors.brandGreen}
          active={userAttending}
          backgroundColorClassName="brandGreen"
          loading={isPending}
          disabled={disableAttendingButton()}
          onClick={() => updateAttendance('attending')}
        />
        <EventAttendanceButton
          label={
            isUpdatingNotAttending
              ? translate('common.eventActions.attendance.updateComment')
              : translate('common.eventActions.attendance.notAttending')
          }
          icon={IconChoices.CROSS}
          iconColor={theme.colors.red}
          active={userNotAttending}
          backgroundColorClassName="red"
          loading={isPending}
          disabled={disableNotAttendingButton()}
          onClick={() => updateAttendance('not_attending')}
        />
      </div>
    </div>
  )
}
